import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useActions } from './ActionsProvider';
import { anyRunning } from './log';

import type { ActionState } from './log';

/**
 * What the console has done lately — GEN-41.
 *
 * Not modal, and nothing to dismiss. A save that worked should not cost a click to
 * acknowledge; a refusal should stay readable while the operator fixes the field. The
 * feed does both by being a list rather than a notification.
 *
 * Lives in the shell, so a screen gets it by existing rather than by remembering.
 */

const COLOUR: Record<ActionState, 'info' | 'success' | 'error'> = {
  running: 'info',
  done: 'success',
  refused: 'error',
};

const LABEL: Record<ActionState, string> = {
  running: 'working',
  done: 'done',
  refused: 'refused',
};

export function ActionFeed() {
  const { actions, dismiss } = useActions();

  if (actions.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {/* One bar for the whole console rather than one per control: the question it
          answers is whether anything is in flight at all. */}
      {anyRunning(actions) && <LinearProgress />}

      {/* Dismissable, and one per action name — GEN-65. Every refusal used to become a
          permanent alert, so retrying a refused save three times left three copies of the
          same sentence stacked above the field being fixed. */}
      {actions
        .filter((action) => action.state === 'refused')
        .map((action) => (
          <Alert
            key={action.id}
            severity="error"
            onClose={() => {
              dismiss(action.id);
            }}
          >
            {action.name} was refused. {action.reason}
          </Alert>
        ))}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {actions.map((action) => (
          <Chip
            key={action.id}
            size="small"
            color={COLOUR[action.state]}
            variant={action.state === 'running' ? 'filled' : 'outlined'}
            label={`${action.name} · ${LABEL[action.state]}`}
          />
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        The last {String(actions.length)} action
        {actions.length === 1 ? '' : 's'}, newest first.
      </Typography>
    </Stack>
  );
}
